/**
 * FraudDetailPanel Component
 * 
 * Side panel or modal showing detailed fraud information:
 * - Risk score breakdown
 * - Transaction timeline
 * - Vendor details
 * - Similar payments
 * - Reviewer notes
 * - Audit log
 */

import React, { useState } from 'react';
import {
  X,
  User,
  Building2,
  FileText,
  Clock,
  CheckCircle,
  AlertTriangle,
  MessageSquare,
  History,
  TrendingUp,
  Shield,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import { cn, formatCurrency, formatDate, formatRelativeTime } from '../../lib/utils';
import { RiskScoreVisualizer } from './RiskScoreVisualizer';
import type {
  FraudPaymentDetail,
  FraudTimelineEvent,
  FraudAuditLogEntry,
  ReviewerNote,
  VendorRiskDetails,
  SimilarPayment,
} from '../../types/fraud';
import type { RiskScoreBreakdown } from '../../types/fraud';

export interface FraudDetailPanelProps {
  /** Payment detail data */
  payment: FraudPaymentDetail | null;
  /** Whether panel is open */
  isOpen: boolean;
  /** Callback when panel closes */
  onClose: () => void;
  /** Callback to add a note */
  onAddNote?: (content: string) => void;
  /** Callback to release payment */
  onRelease?: (paymentId: string) => void;
  /** Callback to deny payment */
  onDeny?: (paymentId: string) => void;
  /** Callback to request more info */
  onRequestInfo?: (paymentId: string) => void;
  /** Callback to view similar payment */
  onViewSimilar?: (paymentId: string) => void;
  /** Callback to view vendor */
  onViewVendor?: (vendorId: string) => void;
  className?: string;
}

/**
 * Timeline component for payment events
 */
const Timeline: React.FC<{ events: FraudTimelineEvent[] }> = ({ events }) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'created':
        return <FileText className="w-4 h-4 text-blue-500" />;
      case 'flagged':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'under_review':
        return <Shield className="w-4 h-4 text-orange-500" />;
      case 'released':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'denied':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-0">
      {events.map((event, index) => (
        <div key={index} className="flex">
          <div className="flex flex-col items-center mr-4">
            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
              {getStatusIcon(event.status)}
            </div>
            {index < events.length - 1 && (
              <div className="w-0.5 h-full bg-gray-200 my-1" />
            )}
          </div>
          <div className="pb-6">
            <p className="text-sm font-medium text-gray-900">
              {event.status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
            </p>
            <p className="text-sm text-gray-600">{event.description}</p>
            <p className="text-xs text-gray-400 mt-1">
              {event.actor && <span className="mr-2">by {event.actor}</span>}
              {formatRelativeTime(event.timestamp)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Audit log component
 */
const AuditLog: React.FC<{ entries: FraudAuditLogEntry[] }> = ({ entries }) => {
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'viewed':
        return <User className="w-4 h-4" />;
      case 'commented':
        return <MessageSquare className="w-4 h-4" />;
      case 'released':
        return <CheckCircle className="w-4 h-4" />;
      case 'denied':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <History className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-3">
      {entries.map((entry) => (
        <div key={entry.id} className="flex items-start space-x-3 text-sm">
          <div className="p-1.5 bg-gray-100 rounded text-gray-500">
            {getActionIcon(entry.action)}
          </div>
          <div className="flex-1">
            <p className="text-gray-900">
              <span className="font-medium">{entry.actor.name}</span>{' '}
              <span className="text-gray-600">{entry.action.replace(/_/g, ' ')}</span>
            </p>
            <p className="text-gray-600">{entry.details}</p>
            <p className="text-xs text-gray-400 mt-1">
              {formatRelativeTime(entry.timestamp)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Notes section component
 */
const NotesSection: React.FC<{
  notes: ReviewerNote[];
  onAddNote?: (content: string) => void;
}> = ({ notes, onAddNote }) => {
  const [newNote, setNewNote] = useState('');

  const handleSubmit = () => {
    if (newNote.trim() && onAddNote) {
      onAddNote(newNote.trim());
      setNewNote('');
    }
  };

  return (
    <div className="space-y-4">
      {notes.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No notes yet</p>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div key={note.id} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm text-gray-900">
                  {note.author.name}
                </span>
                <span className="text-xs text-gray-400">
                  {formatRelativeTime(note.createdAt)}
                </span>
              </div>
              <p className="text-sm text-gray-700">{note.content}</p>
            </div>
          ))}
        </div>
      )}

      {onAddNote && (
        <div className="space-y-2">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add a note..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          />
          <button
            onClick={handleSubmit}
            disabled={!newNote.trim()}
            className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Add Note
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Vendor details section
 */
const VendorDetails: React.FC<{
  vendor: VendorRiskDetails;
  onViewVendor?: (vendorId: string) => void;
}> = ({ vendor, onViewVendor }) => {
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-medium text-gray-900">{vendor.name}</h4>
          <p className="text-sm text-gray-500">ID: {vendor.id}</p>
        </div>
        <span
          className={cn(
            'px-2 py-1 rounded text-xs font-medium',
            vendor.verificationStatus === 'verified'
              ? 'bg-green-100 text-green-800'
              : vendor.verificationStatus === 'pending'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-red-100 text-red-800'
          )}
        >
          {vendor.verificationStatus}
        </span>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500 uppercase">Total Payments</p>
          <p className="text-lg font-semibold text-gray-900">{vendor.totalPayments}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500 uppercase">Total Paid</p>
          <p className="text-lg font-semibold text-gray-900">
            {formatCurrency(vendor.totalPaidCents)}
          </p>
        </div>
        {vendor.trustIndexScore !== undefined && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 uppercase">Trust Index</p>
            <p
              className={cn(
                'text-lg font-semibold',
                vendor.trustIndexScore >= 70
                  ? 'text-green-600'
                  : vendor.trustIndexScore >= 30
                  ? 'text-yellow-600'
                  : 'text-red-600'
              )}
            >
              {vendor.trustIndexScore}/100
            </p>
          </div>
        )}
        {vendor.bankAccountAge !== undefined && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 uppercase">Account Age</p>
            <p className="text-lg font-semibold text-gray-900">
              {vendor.bankAccountAge} days
            </p>
          </div>
        )}
      </div>

      {/* Risk Flags */}
      {vendor.riskFlags.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm font-medium text-red-800 mb-2">Risk Flags</p>
          <ul className="space-y-1">
            {vendor.riskFlags.map((flag, idx) => (
              <li key={idx} className="text-sm text-red-700 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1.5" />
                {flag}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Documents */}
      {vendor.verificationDocuments.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Verification Documents</p>
          <div className="space-y-2">
            {vendor.verificationDocuments.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center">
                  <FileText className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-700">{doc.filename}</span>
                </div>
                <span
                  className={cn(
                    'text-xs px-2 py-0.5 rounded',
                    doc.status === 'verified'
                      ? 'bg-green-100 text-green-800'
                      : doc.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  )}
                >
                  {doc.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {onViewVendor && (
        <button
          onClick={() => onViewVendor(vendor.id)}
          className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
        >
          View Full Vendor Profile
          <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      )}
    </div>
  );
};

/**
 * Similar payments section
 */
const SimilarPayments: React.FC<{
  payments: SimilarPayment[];
  onViewPayment?: (paymentId: string) => void;
}> = ({ payments, onViewPayment }) => {
  if (payments.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic">No similar payments found</p>
    );
  }

  return (
    <div className="space-y-3">
      {payments.map((payment) => (
        <button
          key={payment.id}
          onClick={() => onViewPayment?.(payment.id)}
          className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm text-gray-900">{payment.vendorName}</p>
              <p className="text-xs text-gray-500">{formatDate(payment.date)}</p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-sm text-gray-900">
                {formatCurrency(payment.amountCents)}
              </p>
              <div className="flex items-center justify-end space-x-2 mt-1">
                <span
                  className={cn(
                    'text-xs px-1.5 py-0.5 rounded',
                    payment.status === 'settled'
                      ? 'bg-green-100 text-green-800'
                      : payment.status === 'held'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-gray-100 text-gray-800'
                  )}
                >
                  {payment.status}
                </span>
                <span className="text-xs text-blue-600">
                  {(payment.similarity * 100).toFixed(0)}% match
                </span>
              </div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

/**
 * FraudDetailPanel - Main component
 */
export const FraudDetailPanel: React.FC<FraudDetailPanelProps> = ({
  payment,
  isOpen,
  onClose,
  onAddNote,
  onRelease,
  onDeny,
  onRequestInfo,
  onViewSimilar,
  onViewVendor,
  className,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'vendor' | 'similar' | 'audit'>('overview');

  if (!isOpen || !payment) return null;

  // Build risk breakdown for visualizer
  const riskBreakdown: RiskScoreBreakdown = {
    combined: payment.combinedRiskScore,
    qbScore: payment.qbFraudScore,
    plaidSignalScore: payment.plaidSignalScore,
    vendorVerificationScore: 0.5, // Placeholder
    components: payment.fraudFactors.map((factor) => ({
      name: factor.factor,
      weight: factor.scoreContribution,
      score: factor.scoreContribution,
      contribution: factor.scoreContribution,
      description: factor.description,
    })),
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl z-50 overflow-hidden flex flex-col',
          className
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Fraud Review Details</h2>
            <p className="text-sm text-gray-500">Payment {payment.paymentId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Action Bar */}
        <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <span
              className={cn(
                'px-2 py-1 rounded text-xs font-medium',
                payment.priority === 'urgent' && 'bg-red-100 text-red-800',
                payment.priority === 'high' && 'bg-orange-100 text-orange-800',
                payment.priority === 'medium' && 'bg-yellow-100 text-yellow-800',
                payment.priority === 'low' && 'bg-blue-100 text-blue-800'
              )}
            >
              {payment.priority.toUpperCase()} PRIORITY
            </span>
            <span className="text-sm text-gray-500">
              {payment.timeInQueueMinutes < 60
                ? `${payment.timeInQueueMinutes}m in queue`
                : `${Math.floor(payment.timeInQueueMinutes / 60)}h ${payment.timeInQueueMinutes % 60}m in queue`}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            {onRequestInfo && (
              <button
                onClick={() => onRequestInfo(payment.paymentId)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Request Info
              </button>
            )}
            {onDeny && (
              <button
                onClick={() => onDeny(payment.paymentId)}
                className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
              >
                Deny
              </button>
            )}
            {onRelease && (
              <button
                onClick={() => onRelease(payment.paymentId)}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                Release
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {[
            { id: 'overview', label: 'Overview', icon: Shield },
            { id: 'vendor', label: 'Vendor', icon: Building2 },
            { id: 'similar', label: 'Similar', icon: TrendingUp },
            { id: 'audit', label: 'Audit Log', icon: History },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={cn(
                'flex-1 flex items-center justify-center px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Risk Score */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Assessment</h3>
                <div className="flex justify-center">
                  <RiskScoreVisualizer breakdown={riskBreakdown} size="md" />
                </div>
              </div>

              {/* Payment Details */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Amount</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(payment.amountCents)}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Network</p>
                    <p className="text-lg font-semibold text-gray-900 uppercase">
                      {payment.network.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Vendor</p>
                    <p className="text-lg font-semibold text-gray-900">{payment.vendorName}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Customer</p>
                    <p className="text-lg font-semibold text-gray-900">{payment.customerName}</p>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
                <Timeline events={payment.timeline} />
              </div>

              {/* Notes */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
                <NotesSection
                  notes={payment.notes}
                  onAddNote={onAddNote}
                />
              </div>
            </div>
          )}

          {activeTab === 'vendor' && (
            <VendorDetails
              vendor={payment.vendorDetails}
              onViewVendor={onViewVendor}
            />
          )}

          {activeTab === 'similar' && (
            <SimilarPayments
              payments={payment.similarPayments}
              onViewPayment={onViewSimilar}
            />
          )}

          {activeTab === 'audit' && (
            <AuditLog entries={payment.auditLog} />
          )}
        </div>
      </div>
    </>
  );
};

export default FraudDetailPanel;
